import { motion } from 'framer-motion';
import ThreadContentItem from './ThreadContentItem';
import ChatContentItem from './ChatContentItem';
import { getChatDirection } from '../utils/getChatDirection';
import { getThreadSpeakerColor } from '../utils/getThreadSpeakerColor';
import type { components } from '@pocapan/shared/types/schema';

type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];
type CastRelationship = components['schemas']['ApiV1CastRelationship'];

const MotionWrapper = ({
  children,
  showMotion
}: {
  children: React.ReactNode;
  showMotion: boolean;
}) => (
  <motion.div
    initial={showMotion ? { opacity: 0, y: 5 } : false}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, ease: 'easeInOut' }}
  >
    {children}
  </motion.div>
);

interface SceneContentItemProps {
  content: SceneContent;
  displayContentType: string | undefined;
  index: number;
  contentsLength: number;
  totalCount: number;
  showStarForIndicesFrom: number | null;
  speakerOrderMap: Map<string, number>;
  detailForHeader: CastRelationship | null | undefined;
  showSpeaker?: boolean;
}

const SceneContentItem = ({
  content,
  displayContentType,
  index,
  contentsLength,
  totalCount,
  showStarForIndicesFrom,
  speakerOrderMap,
  detailForHeader,
  showSpeaker = true
}: SceneContentItemProps) => {
  if (!displayContentType) return null;
  if (displayContentType === 'SceneThread' && 'threadType' in content) {
    return (
      <ThreadContentItem
        speaker={content.speaker ?? undefined}
        threadType={content.threadType ?? ''}
        text={content.text}
        backgroundColor={getThreadSpeakerColor(
          speakerOrderMap.get(content.speaker ?? '') ?? 0
        )}
        showStar={showStarForIndicesFrom !== null && index >= showStarForIndicesFrom}
        hasLine={
          totalCount > 0
            ? (content.sequence ?? 0) < totalCount
            : index < contentsLength - 1
        }
        className={content.threadType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
      />
    );
  }

  if (displayContentType === 'SceneChat' && 'chatType' in content) {
    const chatItem = (
      <ChatContentItem
        chatType={content.chatType}
        speaker={content.speaker ?? ''}
        text={content.text}
        pictureUrl={content.pictureUrl}
        direction={getChatDirection(detailForHeader ?? null, content.speaker ?? '')}
        showStar={
          showStarForIndicesFrom !== null && index === showStarForIndicesFrom
        }
        showSpeaker={showSpeaker}
        className={content.chatType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
      />
    );
    return (
      <MotionWrapper
        showMotion={
          showStarForIndicesFrom !== null && index >= showStarForIndicesFrom
        }
      >
        {chatItem}
      </MotionWrapper>
    );
  }

  return null;
};

export default SceneContentItem;
